import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface Rules {
  nbOfQuestions: number;
  fitLimits: boolean;
  range: number[];
  steps: number[];
  segments: number[];
  denominator?: number;
}

interface SubLevel {
  rules: Rules;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: SubLevel[];
}

interface RoundState {
  step: number;
  denominator: number;
  values: number[];
  target: number;
  questionIndex: number;
  totalQuestions: number;
}

const LEVELS = levelsData.levels as Level[];

function randInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function buildRound(rules: Rules): RoundState {
  const denominator = rules.denominator ?? 1;
  const step = rules.steps[randInt(0, rules.steps.length - 1)]!;
  const rangeMin = rules.range[0] ?? 0;
  const rangeMax = rules.range[1] ?? rangeMin + step * 5;

  let actualMax: number;
  if (rules.fitLimits) {
    actualMax = rangeMax;
  } else {
    const segMin = rules.segments[0] ?? 4;
    const segMax = rules.segments[1] ?? 8;
    const segCount = randInt(segMin, segMax);
    actualMax = rangeMin + segCount * step;
    if (actualMax > rangeMax) actualMax = rangeMax;
  }

  const values: number[] = [];
  for (let v = rangeMin; v <= actualMax; v += step) values.push(v);
  if (values.length < 2) values.push(rangeMin + step);

  const targetIdx = randInt(1, values.length - 2);
  const target = values[targetIdx] ?? values[0]!;

  return {
    step,
    denominator,
    values,
    target,
    questionIndex: 0,
    totalQuestions: rules.nbOfQuestions,
  };
}

export function mountGraduatedLineRead(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0]!;

  let subIndex = 0;
  let state: RoundState = buildRound(level.data[0]!.rules);
  let correctCount = 0;
  let totalQuestionsTotal = 0;
  let answer = '';
  level.data.forEach((d) => { totalQuestionsTotal += d.rules.nbOfQuestions; });

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Leia a régua</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage graduated">
      <div class="feedback" aria-live="polite"></div>
      <p class="graduated__progress" aria-live="polite"></p>
      <p class="graduated__prompt">Qual número está marcado pela <strong style="color:#f59e0b;">▼</strong>?</p>
      <div class="graduated__ruler-wrap"></div>
      <div class="graduated__input-row">
        <input class="graduated__input" type="text" inputmode="decimal" pattern="[0-9.,-]*" autocomplete="off" placeholder="?" />
      </div>
      <button class="btn btn--primary graduated__ok" type="button">Confirmar</button>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.graduated__progress')!;
  const rulerWrap = page.querySelector<HTMLElement>('.graduated__ruler-wrap')!;
  const inputEl = page.querySelector<HTMLInputElement>('.graduated__input')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.graduated__ok')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function fmtValue(v: number): string {
    if (state.denominator === 1) return String(v);
    return (v / state.denominator).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
  }

  function parseAnswer(raw: string): number | null {
    const normalized = raw.trim().replace(',', '.');
    if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
    const n = Number(normalized);
    if (!isFinite(n)) return null;
    return n;
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${state.questionIndex + 1} de ${state.totalQuestions}`;
  }

  function renderRuler(): void {
    rulerWrap.innerHTML = '';
    const W = 720;
    const H = 100;
    const padding = 40;
    const innerW = W - padding * 2;
    const n = state.values.length;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.setAttribute('class', 'graduated__ruler');
    svg.setAttribute('role', 'group');

    const base = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    base.setAttribute('x1', String(padding));
    base.setAttribute('x2', String(padding + innerW));
    base.setAttribute('y1', String(H / 2));
    base.setAttribute('y2', String(H / 2));
    base.setAttribute('class', 'graduated__line');
    svg.appendChild(base);

    const maxLabels = 12;
    const stride = Math.max(1, Math.ceil((n - 1) / maxLabels));

    state.values.forEach((v, i) => {
      const x = padding + (innerW * i) / (n - 1);
      const isMajor = i === 0 || i === n - 1 || i % stride === 0;
      const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      tick.setAttribute('x1', String(x));
      tick.setAttribute('x2', String(x));
      tick.setAttribute('y1', String(H / 2 - (isMajor ? 12 : 6)));
      tick.setAttribute('y2', String(H / 2 + (isMajor ? 12 : 6)));
      tick.setAttribute('class', `graduated__tick ${isMajor ? 'graduated__tick--major' : 'graduated__tick--minor'}`);
      svg.appendChild(tick);

      if (isMajor) {
        const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        label.setAttribute('x', String(x));
        label.setAttribute('y', String(H / 2 + 32));
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('class', 'graduated__label');
        label.textContent = fmtValue(v);
        svg.appendChild(label);
      }
    });

    // flecha amarela apontando pra baixo, sobre o tick alvo
    const targetIdx = state.values.indexOf(state.target);
    const arrowX = padding + (innerW * targetIdx) / (n - 1);
    const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    arrow.setAttribute('points', `${arrowX - 12},${H / 2 - 34} ${arrowX + 12},${H / 2 - 34} ${arrowX},${H / 2 - 14}`);
    arrow.setAttribute('class', 'graduated__arrow');
    svg.appendChild(arrow);

    rulerWrap.appendChild(svg);
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function validate(): void {
    const parsed = parseAnswer(answer || inputEl.value);
    if (parsed === null) {
      showFeedback('err');
      ctx.audio.play('wrong');
      inputEl.classList.add('graduated__input--wrong');
      window.setTimeout(() => inputEl.classList.remove('graduated__input--wrong'), 500);
      return;
    }
    const expectedDisplay = state.target / state.denominator;
    if (Math.abs(parsed - expectedDisplay) < 1e-9) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      state.questionIndex++;
      if (state.questionIndex < state.totalQuestions) {
        nextQuestion();
      } else {
        subIndex++;
        if (subIndex < level.data.length) {
          state = buildRound(level.data[subIndex]!.rules);
          inputEl.value = '';
          answer = '';
          renderProgress();
          renderRuler();
          inputEl.focus();
        } else {
          window.setTimeout(finishLevel, 600);
        }
      }
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      inputEl.classList.add('graduated__input--wrong');
      window.setTimeout(() => inputEl.classList.remove('graduated__input--wrong'), 500);
    }
  }

  function nextQuestion(): void {
    state.target = state.values[randInt(1, state.values.length - 2)] ?? state.values[0]!;
    inputEl.value = '';
    answer = '';
    renderProgress();
    renderRuler();
    inputEl.focus();
  }

  okBtn.addEventListener('click', validate);
  inputEl.addEventListener('input', () => { answer = inputEl.value; });
  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { validate(); e.preventDefault(); }
  });

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${totalQuestionsTotal} perguntas.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) next.addEventListener('click', () => { window.location.hash = `/graduated_line_read/${level.level + 1}`; });
  }

  renderProgress();
  renderRuler();
  inputEl.focus();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
